/* assets/pack-size.js */
/* One-shot Pack Size updater that wins the event race, prevents cart.json spam,
   and detects faux UI (buttons/radios/listboxes/shadow-DOM) that don't emit native change. */
(() => {
  const LOG = '[PackSize]';
  const BUSY_ATTR = 'data-pack-busy';
  const SUPPRESS_MS = 12000;
  const PACK_HEADER = 'X-Pack-Origin';
  const CART_RX = /\/cart(?:\/(?:change|update)\.js|\.js|\.json)(?:$|\?|#)/i;

  const log = (...a) => console.log(LOG, ...a);

  const fmt = (cents) => {
    try {
      if (window.Shopify?.formatMoney) {
        return Shopify.formatMoney(cents, window.Shopify.money_format || '£{{amount}}');
      }
    } catch(e){}
    const cur = window.Shopify?.currency?.active || 'GBP';
    return new Intl.NumberFormat(undefined,{style:'currency',currency:cur}).format((cents||0)/100);
  };

  const findCartRoot = (node) =>
    node?.closest?.('#theme-ajax-cart') ||
    node?.closest?.('[data-ajax-cart]') ||
    document.querySelector('#theme-ajax-cart') ||
    document;

  const syncQtyInputs = (row, q) => {
    row.querySelectorAll('input[type="number"], input[name^="updates"]').forEach(inp => {
      inp.value = q;
      inp.setAttribute('value', q);
    });
    const qtyText = row.querySelector('[data-qty]');
    if (qtyText) qtyText.textContent = String(q);
  };

  // ---------- helpers that work across shadow DOM ----------
  const getStartNode = (e) => (e.composedPath && e.composedPath()[0]) || e.target;
  const rootHost = (n) => (n && n.getRootNode && n.getRootNode().host) || null;
  const closestDeep = (node, selector) => {
    for (let n = node; n; n = rootHost(n)) {
      if (n.closest) {
        const m = n.closest(selector);
        if (m) return m;
      }
    }
    return null;
  };

  let inFlightCtrl = null;

  function postChange(lineIndex, quantity, row, selForTotals) {
    // suppress other cart calls briefly
    window.suppressUntil = Date.now() + SUPPRESS_MS;

    if (inFlightCtrl) { try { inFlightCtrl.abort(); } catch(_) {} }
    inFlightCtrl = new AbortController();

    log('POST /cart/change.js', { line: lineIndex, quantity });

    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', [PACK_HEADER]: '1' },
      signal: inFlightCtrl.signal,
      body: JSON.stringify({ line: lineIndex, quantity })
    })
    .then(r => {
      if (!r.ok) throw new Error('cart change ' + r.status);
      return r.json();
    })
    .then(cart => {
      // Correct totals
      const item = cart.items[lineIndex - 1];
      const totalEl = row?.querySelector('[data-line-total]');
      if (item && totalEl) totalEl.textContent = fmt(item.final_line_price);

      const localSubtotal = selForTotals?.closest?.('[data-ajax-cart-content]')?.querySelector('[data-cart-subtotal]');
      const subEl = localSubtotal ||
        findCartRoot(row).querySelector('[data-cart-subtotal]') ||
        document.querySelector('[data-cart-subtotal]');
      if (subEl) subEl.textContent = fmt(cart.items_subtotal_price);

      document.querySelectorAll('[data-bind="itemCount"], [data-cart-count]')
        .forEach(n => n.textContent = String(cart.item_count));

      if (window.Shopify?.theme?.jsAjaxCart?.updateView) {
        Shopify.theme.jsAjaxCart.updateView();
      }
      return cart;
    })
    .finally(() => {
      row?.classList.remove('is-updating');
      findCartRoot(row)?.removeAttribute(BUSY_ATTR);
      inFlightCtrl = null;
    });
  }

  function handlePackChange(sel, explicitMultiplier) {
    const row =
      sel?.closest?.('[data-cart-item]') ||
      sel?.closest?.('[data-line-item]') ||
      sel?.closest?.('[data-line-index]');
    if (!row) return;

    const root = findCartRoot(row);
    root.setAttribute(BUSY_ATTR, '1');

    const lineIndex =
      Number(row.getAttribute('data-line-index')) ||
      Number(row.getAttribute('data-line-item'));
    if (!lineIndex) {
      root.removeAttribute(BUSY_ATTR);
      return;
    }

    const unit   = Number(row.getAttribute('data-unit-price') || '0');
    const base   = Number(row.getAttribute('data-base-qty')  || '1');

    // SAFARI-SAFE: avoid ?? with || — use a plain ternary
    const mult   = (explicitMultiplier != null) ? explicitMultiplier : Number(sel.value);
    const target = base * ((mult || 1));

    const totalEl = row.querySelector('[data-line-total]');
    row.classList.add('is-updating');
    if (totalEl) totalEl.textContent = fmt(unit * target);
    syncQtyInputs(row, target);

    return postChange(lineIndex, target, row, sel);
  }

  // ------------ Busy guard: block qty/UI events during our update ------------
  const guardWhileBusy = (ev) => {
    const anyBusy = document.querySelector(`#theme-ajax-cart[${BUSY_ATTR}], [data-ajax-cart][${BUSY_ATTR}]`);
    if (!anyBusy) return;

    const start = getStartNode(ev);
    const inCart = !!closestDeep(start, '#theme-ajax-cart, [data-ajax-cart], .mobile-header [data-ajax-cart-content]');
    const qtyLike = inCart && (closestDeep(start, '.product-quantity-box') ||
                   (start.matches?.('.quantity-element, input[name^="updates"], input[type="number"]')));

    if (qtyLike) {
      ev.stopImmediatePropagation?.();
      ev.stopPropagation();
      ev.preventDefault();
    }
  };
  ['input','change','mousedown','touchstart','keydown','keyup'].forEach(evt =>
    document.addEventListener(evt, guardWhileBusy, true)
  );

  // ------------ Native select: capture phase ------------
  function onChangeCapture(ev) {
    const start = getStartNode(ev);
    const sel = closestDeep(start, '[data-pack-select]');
    if (!sel) return;
    ev.stopImmediatePropagation?.();
    ev.stopPropagation();
    handlePackChange(sel);
  }
  document.addEventListener('change', onChangeCapture, true);

  // ------------ Safety net: focus+poll if UI changes value without change event ------------
  function startPoll(sel) {
    if (sel.__packPoll) return;
    sel.__packPrev = sel.value;
    sel.__packPoll = setInterval(() => {
      if (!document.body.contains(sel)) return stopPoll(sel);
      if (sel.value !== sel.__packPrev) {
        sel.__packPrev = sel.value;
        handlePackChange(sel);
      }
    }, 120);
  }
  function stopPoll(sel) {
    if (sel.__packPoll) { clearInterval(sel.__packPoll); sel.__packPoll = null; }
  }
  document.addEventListener('focusin', (e) => {
    const sel = closestDeep(getStartNode(e), '[data-pack-select]');
    if (sel) startPoll(sel);
  }, true);
  document.addEventListener('focusout', (e) => {
    const sel = closestDeep(getStartNode(e), '[data-pack-select]');
    if (sel) stopPoll(sel);
  }, true);

  // ------------ Faux UI heuristics (buttons/radios/listbox/pills + shadow DOM) ------------
  const getMultiplierFromEl = (el) => {
    if (!el) return null;
    const ds = el.dataset || {};
    const keys = ['multiplier','qtyMultiplier','pack','value','packValue'];
    for (const k of keys) {
      const v = ds[k];
      if (v && /^\d+$/.test(v)) return Number(v);
    }
    const txt = (el.getAttribute?.('data-label') || el.textContent || '').trim();
    const m = txt.match(/\b(1|6|12)\b/);
    if (m) return Number(m[1]);
    return null;
  };

  document.addEventListener('click', (e) => {
    const start = getStartNode(e);
    const row = closestDeep(start, '[data-cart-item],[data-line-item],[data-line-index]');
    if (!row) return;

    let mult = null;

    const radio = closestDeep(start, 'input[type="radio"][value]');
    if (radio && /^\d+$/.test(radio.value)) mult = Number(radio.value);

    if (mult == null) {
      const btn = closestDeep(start, '[data-pack-option],[data-pack-trigger],[data-qty-multiplier],[data-multiplier],button,a,li,label,[role="option"],[role="button"],[class*="pack"],[class*="qty"]');
      mult = getMultiplierFromEl(btn) ?? getMultiplierFromEl(btn?.parentElement);
      if (mult != null) log('faux click →', mult);
    }

    if (mult != null) {
      const sel = row.querySelector?.('[data-pack-select]');
      if (sel) {
        e.stopImmediatePropagation?.();
        e.stopPropagation();
        sel.value = String(mult);
        sel.__packPrev = sel.value;
        handlePackChange(sel, mult);
      }
    }
  }, true);

  const mo = new MutationObserver((mutations) => {
    mutations.forEach(m => {
      const el = m.target;
      if (m.attributeName === 'aria-selected' && el.getAttribute('aria-selected') === 'true') {
        const row = closestDeep(el, '[data-cart-item],[data-line-item],[data-line-index]');
        const mult = getMultiplierFromEl(el);
        if (row && mult != null) {
          const sel = row.querySelector('[data-pack-select]');
          if (sel) {
            sel.value = String(mult);
            sel.__packPrev = sel.value;
            log('aria-selected →', mult);
            handlePackChange(sel, mult);
          }
        }
      }
    });
  });
  mo.observe(document.documentElement, { attributes: true, attributeFilter: ['aria-selected'], subtree: true });

  // ------------ Suppress other cart calls during our update ------------
  const isSuppressed = (url, headers) => {
    try {
      const ours = !!(headers && (headers[PACK_HEADER] || headers.get?.(PACK_HEADER)));
      return CART_RX.test(url) && Date.now() < (window.suppressUntil || 0) && !ours;
    } catch(e) { return false; }
  };

  if (!window.__packFetchPatched && typeof window.fetch === 'function') {
    const orig = window.fetch.bind(window);
    window.fetch = function(input, init = {}) {
      const url = typeof input === 'string' ? input : (input && input.url) || '';
      const headers = init && init.headers;
      if (isSuppressed(url, headers)) {
        console.log(LOG, 'blocked FETCH', url);
        return Promise.reject(new DOMException('Blocked cart call during pack update', 'AbortError'));
      }
      return orig(input, init);
    };
    window.__packFetchPatched = true;
  }

  if (!window.__packXHRPatched && typeof window.XMLHttpRequest !== 'undefined') {
    const o = XMLHttpRequest.prototype.open;
    const s = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.open = function(method, url){ this.__packUrl = url; return o.apply(this, arguments); };
    XMLHttpRequest.prototype.send = function(body){
      try{
        if (isSuppressed(this.__packUrl)) {
          console.log(LOG, 'blocked XHR', this.__packUrl);
          try { this.abort(); } catch(_){}
          return;
        }
      }catch(_){}
      return s.apply(this, arguments);
    };
    window.__packXHRPatched = true;
  }

  function patchJq() {
    if (window.__packJQPatched || !window.jQuery || !window.jQuery.ajax) return;
    const $ = window.jQuery;
    const origAjax = $.ajax;
    $.ajax = function(url, options){
      let settings = {};
      if (typeof url === 'object') settings = url || {};
      else { settings = options || {}; settings.url = url; }
      const u = settings.url || '';
      const headers = settings.headers || {};
      if (isSuppressed(u, headers)) {
        console.log(LOG, 'blocked jQuery.ajax', u);
        const d = $.Deferred(); setTimeout(() => d.reject(new DOMException('Blocked cart call during pack update','AbortError')), 0);
        return d.promise();
      }
      return origAjax.call(this, settings);
    };
    window.__packJQPatched = true;
  }
  patchJq();
  document.addEventListener('DOMContentLoaded', patchJq);

  if (!window.__packAxiosPatched && window.axios && window.axios.request) {
    const origReq = window.axios.request.bind(window.axios);
    window.axios.request = function(config = {}){
      const url = (config && ((config.baseURL || '') + (config.url || ''))) || '';
      const headers = config && config.headers;
      if (isSuppressed(url, headers)) {
        console.log(LOG, 'blocked axios', url);
        return Promise.reject(new DOMException('Blocked cart call during pack update','AbortError'));
      }
      return origReq(config);
    };
    window.__packAxiosPatched = true;
  }

  if (!window.__packBeaconPatched && navigator.sendBeacon) {
    const origBeacon = navigator.sendBeacon.bind(navigator);
    navigator.sendBeacon = function(url, data){
      if (isSuppressed(url)) {
        console.log(LOG, 'blocked sendBeacon', url);
        return false;
      }
      return origBeacon(url, data);
    };
    window.__packBeaconPatched = true;
  }

  // Public helpers
  window.PackSize = {
    handle: (el) => handlePackChange(el),
    test: (lineIndex, quantity) =>
      fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', [PACK_HEADER]: '1' },
        body: JSON.stringify({ line: lineIndex, quantity })
      }).then(r => r.json()),
    debug: () => {
      const nodes = [...document.querySelectorAll('[data-pack-select]')];
      const out = nodes.map(n => ({
        multiplier: n.value,
        lineIndex: Number(n.closest('[data-line-index],[data-line-item]')?.getAttribute('data-line-index') ||
                          n.closest('[data-line-item]')?.getAttribute('data-line-item') || 0),
        tag: n.tagName,
        classes: n.className
      }));
      console.table(out);
      return out;
    }
  };

  log('armed');
})();
